import trace from '~/lib/trace';
import {useEffect, useState} from 'react';
import {View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import moment from 'moment';

import sy from '~/styles';
import {
  Pressable,
  Text,
  ServiceRequestStatusIcon,
  ReplacementTileIcon,
  UserLabel,
} from '~/components/controls';
import {SERVICE_TYPE_CODES} from '~/types/services';
import Format from '~/lib/format';
import {service_request as serviceRequestApi} from '~/api/private';
import {IsReplacementVehicle, IsDamageRequest} from '~/types/request';

import CarRepairIcon from '~/images/md-icons/car_repair/materialicons/24px.svg';
import ApprovalIcon from '~/images/md-icons/approval/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import BuildIcon from '~/images/md-icons/build_repair/materialicons/24px.svg';
import CarCrashIcon from '~/images/md-icons/car_crash/materialicons/24px.svg';
import CarRentalIcon from '~/images/md-icons/car_rental/materialicons/24px.svg';
import PropTypes from 'prop-types';

const Type = ({type, style}) => {
  if (!type) {
    return null;
  }

  let icon;
  switch (type) {
    case SERVICE_TYPE_CODES.Maintenance:
      icon = <CarRepairIcon fill="#4A4A49" style={style} />;
      break;
    case SERVICE_TYPE_CODES.Inspection:
      icon = <ApprovalIcon fill="#4A4A49" style={style} />;
      break;
    case SERVICE_TYPE_CODES.Tires:
      icon = <TireRepairIcon fill="#4A4A49" style={style} />;
      break;
    case 'DamageRequest':
      icon = <CarCrashIcon fill="#4A4A49" style={style} />;
      break;
    case 'ReplacementCar':
      icon = <CarRentalIcon fill="#4A4A49" style={style} />;
      break;
    default:
      icon = <BuildIcon fill="#4A4A49" style={style} />;
      break;
  }

  return icon;
};
Type.propTypes = {
  type: PropTypes.any,
  style: PropTypes.any,
};

const ServiceRequestCard = ({
  style,
  show_time,
  id,
  datetime,
  status,
  rob_status,
  car_name,
  license,
  lessor,
  kvk_name,
  reference,
  request_type,
  parent_id,
  replacement,
  assigned_user_id,
}) => {
  const navigation = useNavigation();
  const [duration, setDuration] = useState(null);
  const [icons, setIcons] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      const {result} = await serviceRequestApi.calendar_info(id);
      setDuration(result?.duration);
      setIcons(result?.icons);
    };

    fetch().catch(trace);
  }, [id, status, rob_status, request_type]);

  return (
    <Pressable
      style={[
        sy['flex-row'],
        sy['bg-white'],
        sy['py-3'],
        sy['px-4'],
        sy['overflow-hidden'],
        sy['rounded-lg'],
        style,
      ]}
      onPress={() => {
        if (IsDamageRequest(request_type)) {
          navigation.navigate('Damage', {damage_id: id});
        } else if (IsReplacementVehicle(request_type)) {
          navigation.navigate('ReplacementVehicle', {request_id: parent_id});
        } else {
          navigation.navigate('Request', {request_id: id});
        }
      }}>
      <View
        style={[
          sy['items-center'],
          sy['justify-between'],
          {paddingVertical: 2},
          sy['pr-4'],
          {
            borderRightColor: '#DCDCDC',
            borderRightWidth: 1,
          },
        ]}>
        {show_time ? (
          <Text
            style={[
              sy.small,
              sy['text-center'],
              {backgroundColor: '#F2F2F2', borderRadius: 6, width: 40},
            ]}>
            {moment.utc(datetime).format('H:mm')}
          </Text>
        ) : (
          <Text
            style={[
              sy.mediumLarge,
              sy['text-center'],
              {backgroundColor: '#F2F2F2', borderRadius: 6, width: 40},
            ]}>
            {moment(datetime).format('D')}
          </Text>
        )}
        <ServiceRequestStatusIcon status={status} />
      </View>
      <View style={[sy['flex-1'], sy['pl-4']]}>
        <View
          style={[
            sy['flex-row'],
            sy['items-center'],
            sy['gap-2'],
            sy['justify-between'],
          ]}>
          <Text style={[sy.mediumPlus, sy.truncate]}>
            {Format.license(license)}
          </Text>
          <View style={[sy['shrink-0'], sy['flex-row'], sy['gap-2']]}>
            {icons.map((icon) => (
              <Type key={icon} type={icon} style={{height: 18, width: 18}} />
            ))}
            {!IsReplacementVehicle(request_type) && replacement?.type && (
              <ReplacementTileIcon
                request_id={id}
                type={replacement?.type}
                fill="#4A4A49"
                style={{height: 18, width: 18}}
              />
            )}
          </View>
        </View>
        <View style={[sy['flex-row'], sy['gap-2'], sy['justify-between']]}>
          <View style={[sy['flex-1']]}>
            <Text style={[sy.small, sy.truncate]}>{car_name}</Text>
            <Text style={[sy.small, sy.truncate]}>
              {kvk_name ?? lessor}
              {reference && <>&nbsp;&middot;&nbsp;{reference}</>}
            </Text>
          </View>
          <View style={sy['shrink-0']}>
            {duration > 0 && (
              <Text style={[sy.smallRegular]}>
                {Format.number(duration, 2, false, {
                  minimumFractionDigits: 0,
                })}
                &nbsp;u
              </Text>
            )}
          </View>
        </View>
      </View>
      <UserLabel key={id} user_id={assigned_user_id} />
    </Pressable>
  );
};
ServiceRequestCard.propTypes = {
  style: PropTypes.any,
  show_time: PropTypes.any,
  id: PropTypes.any,
  datetime: PropTypes.any,
  status: PropTypes.any,
  rob_status: PropTypes.any,
  car_name: PropTypes.any,
  license: PropTypes.any,
  lessor: PropTypes.any,
  kvk_name: PropTypes.any,
  reference: PropTypes.any,
  request_type: PropTypes.any,
  parent_id: PropTypes.any,
  replacement: PropTypes.any,
  assigned_user_id: PropTypes.any,
};

export default ServiceRequestCard;
